import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { CardElement, injectStripe } from 'react-stripe-elements';
import {
    withStyles,
    Typography,
    withWidth,
    CircularProgress,
    Icon
} from '@material-ui/core';
import FormControl from '@material-ui/core/FormControl';
import FormGroup from '@material-ui/core/FormGroup';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import Checkbox from '@material-ui/core/Checkbox';
import { withRouter } from 'react-router-dom';
import axios from 'axios';
import { red } from '@material-ui/core/colors';
import { Auth } from 'aws-amplify';
import LoaderButton from './LoaderButton';
import { formatPrice } from '../helpers/formatters';
import { getYearGroups } from '../helpers/ticket';
import {
    DEFAULT_ERROR,
    getFriendlyPaymentError,
    isSafePaymentError
} from '../helpers/errors';
import globalStyles from '../styles';

const styles = {
    root: {
        marginTop: '20px',
        minHeight: '100px',
        marginBottom: '20px',
        padding: '0 10px 10px',
        textAlign: 'center'
    },
    card: {
        margin: '30px auto 15px auto',
        maxWidth: '360px',
        border: '1px solid rgba(0, 0, 0, 0.23)',
        borderRadius: '4px',
        padding: '10px 0 10px 8px'
    },
    button: {
        margin: '20px 0 20px',
        width: '200px',
        height: '56px'
    },
    error: {
        margin: '20px 20px 10px',
        color: red[500],
        minHeight: '20px'
    },
    saveCard: {
        display: 'inline',
        marginLeft: 14,
        marginTop: 18
    },
    spinner: {
        marginTop: 60,
        marginBottom: 60,
        marginLeft: 'calc(50% - 22px)'
    },
    icon: {
        fontSize: 25
    },
    existingCardParent: {
        display: 'flex',
        justifyContent: 'center',
        marginTop: 20
    },
    existingCard: {
        ...globalStyles.savedCard,
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        padding: '0 10px',
        flexWrap: 'wrap'
    },
    existingCardHeader: {
        flexBasis: '100%',
        marginTop: 10,
        ...globalStyles.savedCardHeader
    },
    changeCard: {
        width: 'auto',
        height: 'auto',
        padding: '5px',
        lineHeight: '1',
        marginLeft: 10,
        fontSize: 12
    }
};

class RenewalPaymentForm extends Component {
    constructor(props) {
        super(props);

        this.state = {
            processing: false,
            error: '',
            cardError: '',
            cardValid: false,
            paymentError: false,
            saveCard: false,
            saveReplacedCard: false,
            paymentMethods: null,
            isLoadingPaymentMethods: true
        };
    }

    async componentDidMount() {
        try {
            // get customer payment methods
            const { client } = this.props;
            const currentSession = await Auth.currentSession();

            const headers = {
                Authorization: currentSession.getIdToken().getJwtToken()
            };

            const response = await axios.get(
                `${
                    process.env.REACT_APP_PAYMENT_API_ENDPOINT
                }/payment-methods/${
                    currentSession.getAccessToken().payload.sub
                }/${client.slug}`,
                {
                    headers
                }
            );

            // if any, set state
            if (
                response.data &&
                response.data.data &&
                response.data.data.length
            ) {
                this.setState({
                    paymentMethods: response.data.data,
                    isLoadingPaymentMethods: false
                });
            } else {
                this.setState({
                    isLoadingPaymentMethods: false
                });
            }
        } catch (error) {
            console.error(error);
            this.setState({
                isLoadingPaymentMethods: false
            });
        }
    }

    handleSubmit = async () => {
        const { cardError, cardValid, paymentMethods } = this.state;
        const {
            client,
            service,
            stop,
            yr,
            dest,
            zone,
            zoneStop,
            ticket,
            extId
        } = this.props;

        if (
            client &&
            client.features &&
            client.features.require_id === 'student' &&
            !extId
        ) {
            this.setState({
                processing: false,
                error: 'Please provide a student id'
            });
            return;
        }

        const useZones =
            service.zones &&
            ticket.pricing_model === 'Zonal' &&
            ticket.zone_prices;

        if (useZones) {
            const serviceZone =
                zone && service.zones.find(z => z.name === zone);

            if (!serviceZone) {
                this.setState({
                    processing: false,
                    error: 'Please select a zone'
                });
                return;
            }

            if (
                serviceZone.stops &&
                serviceZone.stops.length &&
                !serviceZone.stops.includes(zoneStop)
            ) {
                this.setState({
                    processing: false,
                    error: 'Please select a bus stop'
                });
                return;
            }
        } else if (
            service &&
            'stops' in service &&
            !service.stops.includes(stop)
        ) {
            this.setState({
                error: 'Please select a bus stop'
            });
            return;
        }

        if (service && service.destinations && service.destinations.length) {
            if (!service.destinations.includes(dest)) {
                this.setState({
                    processing: false,
                    error: 'Please select a destination'
                });
                return;
            }
        }

        if (service && getYearGroups(service.yr_groups).length) {
            if (!yr) {
                this.setState({
                    processing: false,
                    error: 'Please select a year group'
                });
                return;
            }
        }

        // Only check for card errors if there is value to pay
        // Full voucher value means no card validation required
        if (Number(this.netPrice()) >= 1) {
            if (cardError) {
                this.setState({
                    error: cardError
                });
                return;
            }
            if (!cardValid && !paymentMethods) {
                this.setState({
                    error: 'Please complete all card details including postcode'
                });
                return;
            }
        } else if (Number(this.netPrice()) > 0) {
            this.setState({
                error: `Invalid amount. Amount can only be 0 or a minimum of ${formatPrice(
                    1,
                    ticket.currency
                )} using card payment.`
            });
            return;
        }

        this.setState({
            processing: true,
            error: ''
        });

        try {
            await this.handleInitialPayment();
        } catch (e) {
            console.error(e);
            const error = getFriendlyPaymentError(e, DEFAULT_ERROR);
            this.setState({
                processing: false,
                error,
                paymentError: !isSafePaymentError(error)
            });
        }
    };

    handleInitialPayment = async () => {
        const {
            stripe,
            history,
            ticket,
            service,
            renewal,
            client,
            voucher,
            stop,
            dest,
            yr,
            zone,
            zoneStop,
            extId
        } = this.props;

        const { saveCard, saveReplacedCard, paymentMethods } = this.state;

        let paymentMethodId;

        // Only create payment method when there is something to charge.
        // A full value voucher will hide card field.
        if (this.netPrice() > 0) {
            if (paymentMethods && paymentMethods.length) {
                paymentMethodId = paymentMethods[0].id;
            } else {
                let paymentError;

                try {
                    const {
                        paymentMethod,
                        error
                    } = await stripe.createPaymentMethod(
                        'card',
                        this.cardElement
                    );

                    if (!error) {
                        paymentMethodId = paymentMethod.id;
                    } else {
                        paymentError = error;
                    }
                } catch (paymentMethodError) {
                    const err = new Error(
                        `Error adding payment method: ${paymentMethodError.message}`
                    );
                    err.force = true;
                    throw err;
                }

                if (paymentError) {
                    if (paymentError.code === 'card_declined') {
                        throw new Error('Card declined');
                    } else if (paymentError.type === 'card_error') {
                        throw new Error('Card error');
                    } else if (paymentError.type === 'invalid_request_error') {
                        const err = new Error('Invalid request');
                        err.force = true;
                        if (paymentError.param) {
                            err.detail = paymentError.param;
                        }
                        throw err;
                    } else {
                        const err = new Error('Card api error');
                        err.force = true;
                        if (paymentError.message) {
                            err.detail = paymentError.message;
                        }
                        throw err;
                    }
                }
            }
        }

        const currentSession = await Auth.currentSession();

        const headers = {
            Authorization: currentSession.getIdToken().getJwtToken()
        };

        const data = {
            payment_method_id: paymentMethodId,
            ticket,
            voucher,
            service,
            client,
            renewal,
            stop,
            dest,
            save_card: saveCard || saveReplacedCard
        };

        if (extId) {
            data.ext_id = extId;
        }

        if (service && getYearGroups(service.yr_groups).length) {
            data.yr = yr;
        }

        const useZones =
            service.zones &&
            ticket.pricing_model === 'Zonal' &&
            ticket.zone_prices;

        if (useZones) {
            data.zone = zone;
            data.zoneStop = zoneStop;
        }

        const response = await axios.post(
            `${process.env.REACT_APP_PAYMENT_API_ENDPOINT}/customer-ticket/stripe/renew`,
            data,
            {
                headers
            }
        );

        if (response.data.id) {
            history.push(`/renewal-confirmed/${response.data.id}`);
            // @NOTE: Hack to jump user to top of page on submit, as the route doesn't change
            // resulting in unexpected landing half way down subsequent view.
            window.scrollTo(0, 0);
        } else if (
            response.data.requires_action &&
            response.data.payment_intent_client_secret
        ) {
            await this.handleActionPayment(response.data);
        } else {
            throw new Error('Problem creating payment intent.');
        }
    };

    handleActionPayment = async paymentResponse => {
        const {
            stripe,
            history,
            ticket,
            service,
            client,
            voucher,
            renewal,
            stop,
            dest,
            zone,
            zoneStop,
            yr,
            extId
        } = this.props;

        const {
            error: errorAction,
            paymentIntent
        } = await stripe.handleCardAction(
            paymentResponse.payment_intent_client_secret
        );

        if (errorAction) {
            throw new Error(errorAction.message);
        } else {
            const currentSession = await Auth.currentSession();

            const headers = {
                Authorization: currentSession.getIdToken().getJwtToken()
            };

            const data = {
                payment_intent_id: paymentIntent.id,
                renewal,
                ticket,
                service,
                voucher,
                client,
                stop,
                dest
            };

            if (extId) {
                data.ext_id = extId;
            }

            if (service && getYearGroups(service.yr_groups).length) {
                data.yr = yr;
            }

            const useZones =
                service.zones &&
                ticket.pricing_model === 'Zonal' &&
                ticket.zone_prices;

            if (useZones) {
                data.zone = zone;
                data.zoneStop = zoneStop;
            }

            const response = await axios.post(
                `${process.env.REACT_APP_PAYMENT_API_ENDPOINT}/customer-ticket/stripe/renew`,
                data,
                {
                    headers
                }
            );

            if (response.data.id) {
                history.push(`/renewal-confirmed/${response.data.id}`);
                // @NOTE: Hack to jump user to top of page on submit, as the route doesn't change
                // resulting in unexpected landing half way down subsequent view.
                window.scrollTo(0, 0);
            } else if (
                response.data.requires_action &&
                response.data.payment_intent_client_secret
            ) {
                await this.handleAction(response.data);
            } else {
                throw new Error('Problem confirming payment intent.');
            }
        }
    };

    handleReady = element => {
        this.cardElement = element;
        this.cardElement.on('change', response => {
            const cardValid = !response.empty && response.complete;
            const cardError = response.error ? response.error.message : '';
            this.setState({
                cardValid,
                cardError
            });
        });
    };

    netPrice = () => {
        const { ticket, voucher, zone } = this.props;

        const useZones =
            ticket.pricing_model === 'Zonal' && ticket.zone_prices && zone;
        const ticketPrice = useZones ? ticket.zone_prices[zone] : ticket.price;

        if (voucher) {
            return ticketPrice - voucher.value;
        }
        return ticketPrice;
    };

    handleCheckboxChange = event => {
        this.setState({ [event.target.name]: event.target.checked });
    };

    render() {
        const { classes, width, client, service, ticket } = this.props;
        const {
            processing,
            error,
            paymentError,
            saveCard,
            saveReplacedCard,
            paymentMethods,
            isLoadingPaymentMethods
        } = this.state;

        const hasPaymentMethods = paymentMethods && paymentMethods.length;

        if (isLoadingPaymentMethods) {
            return (
                <CircularProgress
                    id="loading-spinner"
                    className={classes.spinner}
                />
            );
        }

        return (
            <div className={classes.root}>
                <Typography variant="body1" style={{ paddingBottom: '1em' }}>
                    By completing this purchase, I confirm I have read and
                    accepted the following:
                </Typography>
                {service.terms_url && (
                    <Typography variant="body1">
                        <a
                            href={service.terms_url}
                            rel="noopener noreferrer"
                            target="_blank"
                            style={{ color: '#000' }}
                        >
                            {client.name} T&amp;Cs
                        </a>
                    </Typography>
                )}
                <Typography variant="body1">
                    <a
                        href="https://shuttleid.uk/static/shuttleid-terms-of-use-passengers,-purchasers-and-other-users-updated-august-2020-8c80ca7b2c819e755e149871e5a98e88.pdf"
                        rel="noopener noreferrer"
                        target="_blank"
                        style={{ color: '#000' }}
                    >
                        ShuttleID T&amp;Cs
                    </a>
                </Typography>

                {this.netPrice() >= 1 && (
                    <>
                        {!hasPaymentMethods && (
                            <>
                                <CardElement
                                    onReady={this.handleReady}
                                    className={classes.card}
                                    style={{
                                        base: {
                                            fontFamily: '"Roboto"',
                                            fontSize:
                                                width === 'xs'
                                                    ? '14px'
                                                    : '18px',
                                            color: '#000'
                                        }
                                    }}
                                />
                                {!saveReplacedCard && (
                                    <div>
                                        <FormControl
                                            component="fieldset"
                                            className={classes.saveCard}
                                        >
                                            <FormGroup>
                                                <FormControlLabel
                                                    control={
                                                        <Checkbox
                                                            checked={saveCard}
                                                            onChange={
                                                                this
                                                                    .handleCheckboxChange
                                                            }
                                                            name="saveCard"
                                                        />
                                                    }
                                                    label="Save card details"
                                                />
                                            </FormGroup>
                                        </FormControl>
                                    </div>
                                )}
                            </>
                        )}
                        {hasPaymentMethods && (
                            <div className={classes.existingCardParent}>
                                <div className={classes.existingCard}>
                                    <div className={classes.existingCardHeader}>
                                        Saved card
                                    </div>
                                    <Icon className={classes.icon}>
                                        credit_card
                                    </Icon>
                                    <span
                                        style={{ marginLeft: 5, fontSize: 16 }}
                                    >
                                        ****-****-****-
                                        {paymentMethods[0].card.last4}
                                    </span>
                                    <LoaderButton
                                        className={`${classes.button} ${classes.changeCard}`}
                                        variant="outlined"
                                        color="primary"
                                        type="submit"
                                        disabled={processing}
                                        text="Change"
                                        onClick={() => {
                                            this.setState({
                                                paymentMethods: null,
                                                saveReplacedCard: true
                                            });
                                        }}
                                    />
                                </div>
                            </div>
                        )}
                    </>
                )}
                {error && (
                    <Typography className={classes.error} variant="body2">
                        {error}
                    </Typography>
                )}
                <LoaderButton
                    className={`${classes.button} pay-btn`}
                    variant="outlined"
                    color="primary"
                    type="submit"
                    isLoading={processing}
                    disabled={paymentError}
                    text={`Confirm & pay ${formatPrice(
                        this.netPrice(),
                        ticket.currency
                    )}`}
                    loadingText="Confirming..."
                    onClick={this.handleSubmit}
                />
            </div>
        );
    }
}

RenewalPaymentForm.propTypes = {
    classes: PropTypes.shape().isRequired,
    stripe: PropTypes.shape().isRequired,
    ticket: PropTypes.shape().isRequired,
    service: PropTypes.shape().isRequired,
    client: PropTypes.shape().isRequired,
    renewal: PropTypes.shape().isRequired,
    stop: PropTypes.string,
    dest: PropTypes.string,
    extId: PropTypes.string
};

RenewalPaymentForm.defaultProps = {
    stop: '',
    dest: '',
    extId: ''
};

export default withStyles(styles)(
    injectStripe(withRouter(withWidth()(RenewalPaymentForm)))
);
